import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface UserRole {
  uid: string;
  email: string;
  role: 'user' | 'admin' | 'instructor';
  isAdmin?: boolean;
  createdAt: Date;
  lastLogin: Date;
}

interface AuthContextType {
  currentUser: User | null;
  userRole: UserRole | null;
  isAdmin: boolean;
  isInstructor: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  resendEmailVerification: () => Promise<void>;
  emailVerified: boolean;
  updateUserRole: (uid: string, role: 'user' | 'admin' | 'instructor') => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user role from Firestore
  const fetchUserRole = async (user: User) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserRole;
        setUserRole(userData);
      } else {
        // Create new user with default role
        const newUserRole: UserRole = {
          uid: user.uid,
          email: user.email || '',
          role: 'user',
          createdAt: new Date(),
          lastLogin: new Date(),
        };
        await setDoc(doc(db, 'users', user.uid), newUserRole);
        setUserRole(newUserRole);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserRole(user);
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (userCredential.user) {
      await sendEmailVerification(userCredential.user);
    }
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const resendEmailVerification = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const updateUserRole = async (uid: string, role: 'user' | 'admin' | 'instructor') => {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, { role }, { merge: true });
      
      // Update local state if it's the current user
      if (currentUser?.uid === uid) {
        setUserRole(prev => prev ? { ...prev, role } : null);
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userRole,
    isAdmin: userRole?.role === 'admin' || userRole?.isAdmin === true,
    isInstructor: userRole?.role === 'instructor',
    login,
    signup,
    logout,
    loading,
    signInWithGoogle,
    resendEmailVerification,
    emailVerified: currentUser?.emailVerified ?? false,
    updateUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 